import React, { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Download, Printer, X, School, Loader2, LayoutTemplate } from "lucide-react";
import { doc, getDoc } from "firebase/firestore";
import { db } from "@/firebase/config";

interface SchoolDetails {
  name: string;
  motto?: string;
  address?: string;
  email?: string;
  county?: string;
  system?: string;
  type?: string;
  accountNumber?: string;
  logoUrl?: string;
}

interface PaymentReceiptProps {
  schoolId: string | null;
  transactionInfo: {
    studentName: string;
    regNumber: string;
    amount: number;
    reference: string;
    date: Date;
    method: string;
    className?: string;
    termLabel?: string;
  };
  onClose?: () => void;
}

type ReceiptLayout = "thermal" | "a4-modern" | "a5-classic";

const PaymentReceipt: React.FC<PaymentReceiptProps> = ({ schoolId, transactionInfo, onClose }) => {
  const [school, setSchool] = useState<SchoolDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [layout, setLayout] = useState<ReceiptLayout>("thermal");

  useEffect(() => {
    const fetchSchool = async () => {
      if (!schoolId) {
        setLoading(false);
        return;
      }
      try {
        const docRef = doc(db, "schools", schoolId, "schoolDetails", "main");
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          setSchool(docSnap.data() as SchoolDetails);
        }
      } catch (err) {
        console.error("Failed to fetch school details for receipt", err);
      } finally {
        setLoading(false);
      }
    };
    fetchSchool();
  }, [schoolId]);

  const handlePrint = () => {
    window.print();
  };

  const renderSchoolHeader = (className: string = "", logoSize: string = "h-16 w-16") => (
    <div className={`text-center space-y-1 mb-6 ${className}`}>
      {school?.logoUrl ? (
        <div className="flex justify-center mb-3">
          <img src={school.logoUrl} alt="School Logo" className={`${logoSize} object-contain grayscale print:grayscale`} />
        </div>
      ) : (
        <div className="flex justify-center mb-3">
          <div className={`${logoSize} rounded-full border-2 border-zinc-300 flex items-center justify-center`}>
            <School className="h-1/2 w-1/2 text-zinc-400" />
          </div>
        </div>
      )}
      
      <h2 className="text-xl font-bold tracking-widest text-zinc-900 uppercase">
        {school?.name || "OFFICIAL RECEIPT"}
      </h2>
      <p className="text-xs text-zinc-600 uppercase font-semibold">
        {school?.motto || "Payment Acknowledgement"}
      </p>
      
      {school?.address && (
        <p className="text-xs text-zinc-500 pt-1">
          PO BOX: {school.address} {school.county && `- ${school.county}`}
        </p>
      )}
      {school?.email && (
        <p className="text-xs text-zinc-500">
          {school.email}
        </p>
      )}
    </div>
  );

  return (
    <div className="flex flex-col items-center justify-center w-full py-6">
      {/* Controls */}
      <div className="flex flex-wrap gap-4 mb-6 print:hidden justify-center items-center bg-white dark:bg-zinc-900 border px-6 py-4 rounded-full shadow-sm">
        <div className="flex items-center gap-2 border-r pr-4">
          <LayoutTemplate className="h-4 w-4 text-zinc-500" />
          <span className="text-sm font-medium pr-2">Design:</span>
          {["thermal", "a4-modern", "a5-classic"].map((l) => (
            <Button 
              key={l}
              variant={layout === l ? "default" : "outline"} 
              size="sm"
              onClick={() => setLayout(l as ReceiptLayout)}
              className={layout === l ? "bg-blue-600 hover:bg-blue-700" : ""}
            >
              {l === "thermal" ? "Thermal POS" : l === "a4-modern" ? "A4 Modern" : "A5 Classic"}
            </Button>
          ))}
        </div>
        
        <Button onClick={handlePrint} className="bg-green-600 hover:bg-green-700 text-white gap-2 shadow-sm rounded-full px-6 ml-2">
          <Printer className="h-4 w-4" />
          Print
        </Button>
        {onClose && (
          <Button variant="ghost" onClick={onClose} className="rounded-full gap-2 text-zinc-500">
            <X className="h-4 w-4" />
          </Button>
        )}
      </div>

      {loading ? (
        <div className="flex justify-center py-20">
          <Loader2 className="h-8 w-8 animate-spin text-zinc-400" />
        </div>
      ) : (
        <>
          {/* THERMAL DESIGN */}
          {layout === "thermal" && (
            <div id="printable-receipt" style={{ backgroundColor: 'white', color: 'black' }} className={`w-[85mm] min-h-[100px] p-6 shadow-xl print:shadow-none print:w-[85mm] print:mx-auto font-mono text-black rounded-none relative overflow-hidden transition-all duration-300 print:bg-white print:text-black`}>
              {/* Zig-Zag bottom border effect */}
              <div style={{ backgroundImage: "radial-gradient(circle at 10px 0, transparent 10px, white 11px)" }} className="absolute bottom-0 left-0 w-full h-[15px] bg-[length:20px_20px] bg-repeat-x rotate-180 print:-bottom-[2px]"></div>
              
              {renderSchoolHeader()}

              <div className="border-t-2 border-dashed border-zinc-300 my-4"></div>

              <div className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-zinc-500">Receipt No:</span>
                  <span className="font-semibold">{transactionInfo.reference}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-zinc-500">Date:</span>
                  <span className="font-semibold">{transactionInfo.date.toLocaleString()}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-zinc-500">Method:</span>
                  <span className="font-semibold uppercase">{transactionInfo.method}</span>
                </div>
                {transactionInfo.termLabel && (
                  <div className="flex justify-between">
                    <span className="text-zinc-500">Term:</span>
                    <span className="font-semibold uppercase">{transactionInfo.termLabel}</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-zinc-500">Served By:</span>
                  <span className="font-semibold">Admin</span>
                </div>
              </div>

              <div className="border-t-2 border-dashed border-zinc-300 my-4"></div>

              <div className="space-y-4">
                <div className="text-sm">
                  <span className="text-zinc-500 block mb-1">Received From:</span>
                  <div className="font-semibold text-base leading-tight">
                    {transactionInfo.studentName}
                  </div>
                  <div className="text-zinc-600 mt-1">
                    REG: {transactionInfo.regNumber}
                  </div>
                  {transactionInfo.className && (
                    <div className="text-zinc-600">
                      CLASS: {transactionInfo.className}
                    </div>
                  )}
                </div>
              </div>

              <div className="border-t-2 border-dashed border-zinc-300 my-4"></div>

              <div className="grid grid-cols-[1fr_auto] gap-2 items-center text-sm font-semibold mb-2">
                <span>ITEM</span>
                <span>TOTAL</span>
              </div>
              <div className="grid grid-cols-[1fr_auto] gap-2 items-center text-sm mb-4">
                <span>Fee Collection</span>
                <span>{transactionInfo.amount.toLocaleString()}</span>
              </div>

              <div className="border-t-2 border-zinc-900 my-4 border-2"></div>

              <div className="flex justify-between items-end mb-8">
                <span className="text-sm font-bold text-zinc-500 uppercase">Total Amount</span>
                <div className="text-2xl font-black tracking-tight text-zinc-900">
                  KES {transactionInfo.amount.toLocaleString()}
                </div>
              </div>

              <div className="text-center space-y-2 text-xs text-zinc-500 mt-8 mb-4">
                <p>Thank you for your payment.</p>
                <p>Retain this receipt for your records.</p>
                <p className="font-semibold mt-4 text-black">*** CUSTOMER COPY ***</p>
              </div>
            </div>
          )}

          {/* A4 MODERN DESIGN */}
          {layout === "a4-modern" && (
            <div id="printable-receipt" style={{ backgroundColor: 'white', color: 'black' }} className={`w-full max-w-[210mm] min-h-[297mm] p-12 shadow-xl print:shadow-none print:p-0 print:border-0 relative font-sans transition-all duration-300 print:bg-white print:text-black box-border mx-auto`}>
              <div className="absolute top-0 right-0 w-64 h-64 bg-blue-50/50 rounded-bl-[100px] -z-10 print:bg-transparent"></div>
              
              <div className="flex justify-between items-start mb-12">
                <div className="text-left max-w-[60%]">
                  {school?.logoUrl ? (
                    <img src={school.logoUrl} alt="Logo" className="h-20 w-auto mb-4" />
                  ) : <School className="h-16 w-16 text-blue-600 mb-4" />}
                  <h1 className="text-3xl font-bold text-blue-900 uppercase tracking-wide">{school?.name || "OFFICIAL RECEIPT"}</h1>
                  <p className="text-sm text-zinc-500 mt-2 line-clamp-2">{school?.address} {school?.county}</p>
                  <p className="text-sm text-zinc-500">{school?.email}</p>
                </div>
                
                <div className="text-right">
                  <h2 className="text-4xl font-light text-zinc-300 uppercase tracking-widest mb-4">RECEIPT</h2>
                  <div className="space-y-1 text-sm text-zinc-600">
                    <p><span className="font-semibold text-zinc-900 w-24 inline-block">Receipt No:</span> {transactionInfo.reference}</p>
                    <p><span className="font-semibold text-zinc-900 w-24 inline-block">Date:</span> {transactionInfo.date.toLocaleDateString()}</p>
                    <p><span className="font-semibold text-zinc-900 w-24 inline-block">Payment By:</span> {transactionInfo.method}</p>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-8 mb-12 bg-zinc-50 p-6 rounded-lg border border-zinc-200">
                <div>
                  <h3 className="text-xs font-bold text-zinc-400 uppercase tracking-wider mb-2">Billed To / Received From</h3>
                  <p className="text-lg font-bold text-blue-950">{transactionInfo.studentName}</p>
                  <div className="text-zinc-600 mt-2 space-y-1 text-sm">
                    <p><span className="font-medium text-zinc-500">Reg No:</span> {transactionInfo.regNumber}</p>
                    {transactionInfo.className && <p><span className="font-medium text-zinc-500">Class:</span> {transactionInfo.className}</p>}
                  </div>
                </div>
                <div>
                  <h3 className="text-xs font-bold text-zinc-400 uppercase tracking-wider mb-2">Payment Details</h3>
                  <div className="text-zinc-600 space-y-1 text-sm">
                    {transactionInfo.termLabel && <p><span className="font-medium text-zinc-500">Allocated Term:</span> {transactionInfo.termLabel}</p>}
                    <p><span className="font-medium text-zinc-500">Served By:</span> System Admin</p>
                    <p><span className="font-medium text-zinc-500">Status:</span> <span className="text-green-600 font-semibold uppercase">Paid in Full</span></p>
                  </div>
                </div>
              </div>

              <table className="w-full mb-12 border-collapse">
                <thead>
                  <tr className="border-b-2 border-zinc-200">
                    <th className="py-4 text-left text-sm font-semibold text-zinc-500 uppercase">Description</th>
                    <th className="py-4 text-right text-sm font-semibold text-zinc-500 uppercase">Amount (KES)</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-zinc-100">
                    <td className="py-6 text-zinc-800 font-medium">Fee Collection / Payment Deposit</td>
                    <td className="py-6 text-right text-zinc-800 font-medium">{transactionInfo.amount.toLocaleString()}</td>
                  </tr>
                </tbody>
              </table>

              <div className="flex justify-end mb-16">
                <div className="w-80 bg-blue-50/50 p-6 rounded-lg border border-blue-100 text-right">
                  <p className="text-zinc-500 text-sm mb-2 uppercase tracking-wide font-semibold">Total Received</p>
                  <p className="text-4xl font-bold text-blue-900">KES {transactionInfo.amount.toLocaleString()}</p>
                </div>
              </div>

              <div className="flex justify-between items-end border-t border-zinc-200 pt-8 mt-auto absolute bottom-12 left-12 right-12">
                <div className="text-xs text-zinc-500">
                  <p className="text-black"><strong>Note:</strong> All payments are final. Keep this receipt securely.</p>
                  <p className="mt-1">Generated electronically on {new Date().toLocaleString()}</p>
                </div>
                <div>
                  <div className="h-16 w-48 border-b border-zinc-400 mb-2"></div>
                  <p className="text-center text-xs text-zinc-500 text-black">Authorized Signature</p>
                </div>
              </div>
            </div>
          )}

          {/* A5 CLASSIC DESIGN */}
          {layout === "a5-classic" && (
             <div id="printable-receipt" style={{ backgroundColor: 'white', color: 'black' }} className={`w-full max-w-[210mm] min-h-[148mm] p-8 border-2 border-black shadow-xl print:shadow-none print:border-2 print:border-black relative font-serif transition-all duration-300 flex flex-col text-black print:bg-white print:text-black box-border mx-auto`}>
              <div className="flex justify-between items-center border-b-2 border-black pb-6 mb-6">
                <div className="flex items-center gap-4">
                   {school?.logoUrl ? (
                    <img src={school.logoUrl} alt="Logo" className="h-16 w-16" />
                  ) : <School className="h-12 w-12 text-zinc-600" />}
                  <div>
                    <h1 className="text-2xl font-bold text-black uppercase">{school?.name || "SCHOOL OFFICIAL RECEIPT"}</h1>
                    <p className="text-sm text-black/80">{school?.address} {school?.county}</p>
                  </div>
                </div>
                <div className="text-right border-l-2 border-black pl-6">
                  <p className="text-lg font-bold text-red-600 print:text-black mb-1">No. {transactionInfo.reference}</p>
                  <p className="text-sm text-black font-sans">Date: {transactionInfo.date.toLocaleDateString()}</p>
                </div>
              </div>

              <div className="space-y-6 flex-1 text-sm md:text-base leading-loose">
                <p>
                  Received with thanks from <strong className="border-b border-black px-2 inline-block min-w-[200px] uppercase">{transactionInfo.studentName}</strong> 
                  <span className="ml-4">Reg No:</span> <strong className="border-b border-black px-2 uppercase">{transactionInfo.regNumber}</strong>
                </p>
                <div className="flex gap-4">
                  {transactionInfo.className && (
                    <p>Class: <strong className="border-b border-black px-2 uppercase">{transactionInfo.className}</strong></p>
                  )}
                  {transactionInfo.termLabel && (
                    <p>For Term: <strong className="border-b border-black px-2 uppercase">{transactionInfo.termLabel}</strong></p>
                  )}
                </div>
                <p>
                  The sum of KES (in figures) <strong className="border-2 border-black px-4 py-1 text-lg whitespace-nowrap">{transactionInfo.amount.toLocaleString()}</strong> 
                  <span className="ml-4">by</span> <strong className="border-b border-black px-2 uppercase">{transactionInfo.method}</strong>.
                </p>
                <p>
                  Being payment for <strong className="border-b border-black px-2 uppercase inline-block min-w-[250px]">School Fees Allocation</strong>
                </p>
              </div>

              <div className="flex justify-between items-end mt-12 pt-6">
                <div className="italic text-black/80 text-sm font-sans">
                  "Education is the key to success"
                </div>
                <div>
                   <p className="text-black text-sm mb-1 uppercase font-semibold text-center">Cashier / Accountant</p>
                   <div className="h-0 border-b-2 border-black w-48 mt-8"></div>
                </div>
              </div>
             </div>
          )}

        </>
      )}

      <style dangerouslySetInnerHTML={{__html:`
        @media print {
          body * {
            visibility: hidden;
          }
          #printable-receipt, #printable-receipt * {
            visibility: visible;
          }
          #printable-receipt {
            position: absolute;
            left: 0;
            top: 0;
            margin: 0;
            padding: ${layout === 'a4-modern' ? '0' : '20px'};
            width: 100%;
            background: white !important;
            border: ${layout === 'a5-classic' ? '2px solid #000' : 'none'} !important;
            box-shadow: none !important;
            break-inside: avoid;
          }
          
          /* Ensures colored elements appear in PDF properly */
          * {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
            color-adjust: exact !important;
          }
        }
      `}} />
    </div>
  );
};

export default PaymentReceipt;
